// The two live regions of the game page (R2052, ui-map-interface.md §15
// "The live regions"). A screen reader reads them, and the page never
// paints them (`#live` of `ui/styles.css`).
//
// - The polite region (`role="status"`) says each notice, toast and new
//   advisor line once. It speaks at most once in `LIVE_GAP_MS`: the lines
//   that arrive inside the gap wait and go out together, at most
//   `LIVE_LINES` of them and one count line for the rest, so a burst of
//   notices does not cut each line off with the next.
// - The assertive region (`role="alert"`) says a siege warning, the hold
//   under fire, a fleet lost and a refused order at once. A siege warning
//   reaches every player at the same moment, a promise of the product
//   (PRODUCT.md "Fairness is the promise").
//
// Neither region says the same words twice inside `LIVE_REPEAT_MS`: a
// held refused button repeats its refusal, and the toast restarts its
// line in place of a second copy (§15 "The toast").
//
// The host stands on `document.body` and not inside `#ui`, so the chrome
// veil of the season summary and of photo mode never hides it. The
// module holds no game state; `Overlay` routes each line to it. Every
// line it says comes from the sentence layer: a notice carries the
// server's words, and the module's own words are `gen/strings` `live`.

import { LIVE_GAP_MS, LIVE_LINES, LIVE_REPEAT_MS, LIVE_URGENT_EVENTS } from '../gen/presentation';
import { live as S } from '../gen/strings';
import { REFUSAL } from '../events';
import type { NoticeOptions } from './notices';

/**
 * Whether a notice speaks in the assertive region: a refused order (the
 * `refusal` events, and an error notice with no event, which the client
 * raised itself) or an event of an urgent push class
 * (`[presentation.live] urgent_push`). Every other notice is polite.
 */
export function urgentNotice(opts: NoticeOptions): boolean {
  const key = opts.key ?? '';
  if (key === '') return opts.error === true;
  if (key === REFUSAL || key.startsWith(`${REFUSAL}_`)) return true;
  return LIVE_URGENT_EVENTS.includes(key);
}

/** One line of a region: a new node each time, so a reader says it. */
function line(text: string): HTMLElement {
  const d = document.createElement('div');
  d.textContent = text;
  return d;
}

export class LiveRegions {
  /** The host of both regions, `#live`. */
  readonly el = document.createElement('div');
  readonly polite = document.createElement('div');
  readonly urgent = document.createElement('div');
  /** The lines that wait for the end of the gap. */
  private queue: string[] = [];
  private timer: ReturnType<typeof setTimeout> | null = null;
  /** When the polite region last spoke. */
  private spokeAt = -Infinity;
  /** The words said, and when, for the repeat window. */
  private said = new Map<string, number>();

  constructor(parent: HTMLElement = document.body) {
    this.el.id = 'live';
    this.polite.setAttribute('role', 'status');
    this.polite.setAttribute('aria-live', 'polite');
    this.urgent.setAttribute('role', 'alert');
    this.urgent.setAttribute('aria-live', 'assertive');
    this.el.append(this.polite, this.urgent);
    parent.appendChild(this.el);
  }

  /** One notice or toast, in the region its options name. */
  notice(text: string, opts: NoticeOptions): void {
    if (urgentNotice(opts)) this.alarm(text);
    else this.say(text);
  }

  /** One line for the polite region. */
  say(text: string): void {
    if (!this.fresh(text)) return;
    this.queue.push(text);
    if (this.timer !== null) return;
    const wait = this.spokeAt + LIVE_GAP_MS - Date.now();
    if (wait <= 0) this.flush();
    else this.timer = setTimeout(() => this.flush(), wait);
  }

  /** One line for the assertive region. It speaks at once. */
  alarm(text: string): void {
    if (!this.fresh(text)) return;
    this.urgent.replaceChildren(line(text));
  }

  /** Takes the host off the page and stops the wait. */
  destroy(): void {
    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = null;
    this.queue = [];
    this.el.remove();
  }

  /** Says the waiting lines in the polite region. */
  private flush(): void {
    this.timer = null;
    const lines = this.queue.splice(0);
    if (lines.length === 0) return;
    const shown = lines.slice(0, LIVE_LINES).map(line);
    const rest = lines.length - LIVE_LINES;
    if (rest > 0) shown.push(line(S.more({ n: rest })));
    this.polite.replaceChildren(...shown);
    this.spokeAt = Date.now();
  }

  /** Whether these words were not said inside the repeat window; it
   *  marks them said. An empty text is never fresh. */
  private fresh(text: string): boolean {
    if (!text) return false;
    const now = Date.now();
    for (const [t, at] of this.said) if (now - at >= LIVE_REPEAT_MS) this.said.delete(t);
    if (this.said.has(text)) return false;
    this.said.set(text, now);
    return true;
  }
}
